export const PAYMENT_PROOF_EXT_TO_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
} as const;

export type PaymentProofExt = keyof typeof PAYMENT_PROOF_EXT_TO_MIME;

export type PaymentProofMime =
  (typeof PAYMENT_PROOF_EXT_TO_MIME)[PaymentProofExt];

function isPaymentProofExt(ext: string): ext is PaymentProofExt {
  return Object.prototype.hasOwnProperty.call(PAYMENT_PROOF_EXT_TO_MIME, ext);
}

export const PAYMENT_PROOF_UPLOAD_CONSTRAINTS = {
  maxFiles: 5,
  minBytes: 10 * 1024,
  maxBytes: 2 * 1024 * 1024,
  minWidth: 100,
  minHeight: 100,
  extToMime: PAYMENT_PROOF_EXT_TO_MIME,
} as const;

export const PAYMENT_PROOF_DROPZONE_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

export type PaymentProofImage = {
  mime: PaymentProofMime;
  bytes: number;
  width: number;
  height: number;
};

export type PaymentProofUploadRejectCode =
  | 'too_many_files'
  | 'file_too_small'
  | 'file_too_large'
  | 'invalid_type'
  | 'resolution_too_small'
  | 'undecodable_image';

export const PAYMENT_PROOF_REJECT_COPY = {
  too_many_files: `Maksimal ${PAYMENT_PROOF_UPLOAD_CONSTRAINTS.maxFiles} file yang dapat diunggah`,
  file_too_small: 'File terlalu kecil (minimal 10 KB). Unggah foto asli tanpa kompresi.',
  file_too_large: 'File terlalu besar (maksimal 2 MB). Perkecil foto lalu coba lagi.',
  invalid_type: 'Format tidak didukung. Unggah file JPG atau PNG.',
  resolution_too_small:
    'Gambar terlalu kecil atau buram. Unggah foto asli dengan kualitas lebih baik.',
  undecodable_image:
    'Gambar tidak terbaca. Unggah ulang dengan kualitas yang lebih baik.',
} as const satisfies Record<PaymentProofUploadRejectCode, string>;

export const PAYMENT_PROOF_HELPER_TEXT =
  'Hanya menerima format JPG atau PNG, 10 KB-2 MB, minimal 100x100 piksel';

export type PaymentProofInspectResult =
  | { ok: true; image: PaymentProofImage }
  | { ok: false; code: PaymentProofUploadRejectCode };

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

function readUInt16BE(bytes: Uint8Array, offset: number): number | undefined {
  const high = bytes[offset];
  const low = bytes[offset + 1];
  if (high === undefined || low === undefined) {
    return undefined;
  }
  return (high << 8) | low;
}

function readUInt32BE(bytes: Uint8Array, offset: number): number | undefined {
  const b0 = bytes[offset];
  const b1 = bytes[offset + 1];
  const b2 = bytes[offset + 2];
  const b3 = bytes[offset + 3];
  if (
    b0 === undefined ||
    b1 === undefined ||
    b2 === undefined ||
    b3 === undefined
  ) {
    return undefined;
  }
  return ((b0 << 24) | (b1 << 16) | (b2 << 8) | b3) >>> 0;
}

export function lookupPaymentProofMime(
  filename: string
): PaymentProofMime | undefined {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) {
    return undefined;
  }

  const ext = filename.slice(dot + 1).toLowerCase();
  if (!isPaymentProofExt(ext)) {
    return undefined;
  }

  return PAYMENT_PROOF_EXT_TO_MIME[ext];
}

export function paymentProofRejectMessage(
  code: PaymentProofUploadRejectCode
): string {
  switch (code) {
    case 'too_many_files':
      return PAYMENT_PROOF_REJECT_COPY.too_many_files;
    case 'file_too_small':
      return PAYMENT_PROOF_REJECT_COPY.file_too_small;
    case 'file_too_large':
      return PAYMENT_PROOF_REJECT_COPY.file_too_large;
    case 'invalid_type':
      return PAYMENT_PROOF_REJECT_COPY.invalid_type;
    case 'resolution_too_small':
      return PAYMENT_PROOF_REJECT_COPY.resolution_too_small;
    case 'undecodable_image':
      return PAYMENT_PROOF_REJECT_COPY.undecodable_image;
    default: {
      const _exhaustive: never = code;
      return _exhaustive;
    }
  }
}

function readPngSize(
  bytes: Uint8Array
): { width: number; height: number } | undefined {
  for (let i = 0; i < PNG_SIGNATURE.length; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) {
      return undefined;
    }
  }

  const width = readUInt32BE(bytes, 16);
  const height = readUInt32BE(bytes, 20);
  if (width === undefined || height === undefined || width < 1 || height < 1) {
    return undefined;
  }

  return { width, height };
}

function readJpegSize(
  bytes: Uint8Array
): { width: number; height: number } | undefined {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return undefined;
  }

  let offset = 2;
  while (offset + 9 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }

    const marker = bytes[offset + 1];
    if (marker === undefined) {
      return undefined;
    }

    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      offset += 2;
      continue;
    }

    const segmentLength = readUInt16BE(bytes, offset + 2);
    if (segmentLength === undefined || segmentLength < 2) {
      return undefined;
    }

    if (marker === 0xc0 || marker === 0xc1 || marker === 0xc2) {
      const height = readUInt16BE(bytes, offset + 5);
      const width = readUInt16BE(bytes, offset + 7);
      if (
        width === undefined ||
        height === undefined ||
        width < 1 ||
        height < 1
      ) {
        return undefined;
      }
      return { width, height };
    }

    offset += 2 + segmentLength;
  }

  return undefined;
}

function readImageSize(
  mime: PaymentProofMime,
  bytes: Uint8Array
): { width: number; height: number } | undefined {
  switch (mime) {
    case 'image/png':
      return readPngSize(bytes);
    case 'image/jpeg':
      return readJpegSize(bytes);
    default: {
      const _exhaustive: never = mime;
      return _exhaustive;
    }
  }
}

export function inspectPaymentProofBytes(
  filename: string,
  bytes: Uint8Array
): PaymentProofInspectResult {
  const mime = lookupPaymentProofMime(filename);
  if (mime === undefined) {
    return { ok: false, code: 'invalid_type' };
  }

  const { minBytes, maxBytes, minWidth, minHeight } =
    PAYMENT_PROOF_UPLOAD_CONSTRAINTS;

  if (bytes.length < minBytes) {
    return { ok: false, code: 'file_too_small' };
  }

  if (bytes.length > maxBytes) {
    return { ok: false, code: 'file_too_large' };
  }

  const size = readImageSize(mime, bytes);
  if (size === undefined) {
    return { ok: false, code: 'undecodable_image' };
  }

  if (size.width < minWidth || size.height < minHeight) {
    return { ok: false, code: 'resolution_too_small' };
  }

  return {
    ok: true,
    image: {
      mime,
      bytes: bytes.length,
      width: size.width,
      height: size.height,
    },
  };
}

export function inspectPaymentProofFileCount(
  fileCount: number
): PaymentProofUploadRejectCode | undefined {
  if (fileCount > PAYMENT_PROOF_UPLOAD_CONSTRAINTS.maxFiles) {
    return 'too_many_files';
  }
  return undefined;
}
